import React, { useCallback, useEffect, useState } from 'react';
import { toast } from '@/components/toast/toast';
import EditSignatureBody from './EditSignature';
import routeConfig from '../../config/routeConfig';
import { useHistory } from 'react-router-dom';
import endpointConfig from '../../config/endpointConfig';
import {
	QUERY_SIGNATURE_KEY,
	QUERY_SIGNATURE_NEW,
} from '../request-signature/PersonalInfo';
import { Certificate } from '../../entities/certificate';
import { rawHttp } from '../../http';
import { useSidebar } from '../../provider/sidebar/sidebar-context';
import { getUserCertificates } from '@/use-cases/get-user-signatures';
import notifyHB from '@/config/notifyHB';
import { DataTable } from '@/components/data-table/data-table';
import { ColumnDef } from '@tanstack/react-table';
import { Button } from '@/components/ui/button';
import { DateTime } from 'luxon';
import { BadgeCheck, CircleX, Download, Pencil, Plus } from 'lucide-react';
import styles from './my-signatures.module.scss';
import { AlertDialog, AlertDialogContent } from '@/components/ui/alert-dialog';
import { Alert } from '@/components/ui/alert';
import { cn } from '@/lib/utils';

type UserInfo = {
	id_token: string;
	access_token: string;
	refresh_token: string;
	expires_in: number;
	token_type: string;
};

const TABLE_DATA: Certificate[] = [];

export type EditActionResult = {
	status: 'success' | 'error';
	description: string;
};

const MySignatures: React.FC = () => {
	const { hideSidebar } = useSidebar();
	const history = useHistory();

	const handleOpen = (certificate: Certificate) => {
		setSelectedCertificate(certificate);
	};

	const handleClose = () => {
		setSelectedCertificate(null);
	};

	const fetchCertificates = useCallback(async () => {
		try {
			const newData = await getUserCertificates();
			if (newData) {
				setUserCertificates(
					newData.length === 0 ? TABLE_DATA : newData,
				);
			}
		} catch (exception) {
			console.debug(exception);
			notifyHB(
				'my-signatures',
				'MySignatures',
				`Failed to fetch certificates with ${exception}`,
			);
		}
	}, []);

	useEffect(() => {
		fetchCertificates();
	}, []);

	const [userCertificates, setUserCertificates] = useState(TABLE_DATA);
	const [selectedCertificate, setSelectedCertificate] =
		useState<Certificate | null>(null);
	const [editResult, setEditResult] = useState<EditActionResult | null>(null);

	const handleDownload = async (certificateSerial: string) => {
		try {
			const getToken: UserInfo = JSON.parse(
				localStorage.getItem('user-info') || '{}',
			);

			const response = await rawHttp.get<{
				download_url: string;
			}>(endpointConfig.downloadCertificate(certificateSerial), {
				headers: {
					Authorization: `Bearer ${getToken['access_token']}`,
				},
			});

			window.open(response.data.download_url, '_blank');
		} catch (error) {
			toast({ mode: 'destructive', description: 'Algo salió mal, intenta de nuevo más tarde' });
			console.error(error);
		}
	};

	const myCertificatesTableData: ColumnDef<Certificate>[] = [
		{
			accessorKey: 'type',
			header: () => 'Tipo',
			cell: ({ row }) => (
				<div className={styles['certificate-type']}>
					{row.original.type}
				</div>
			),
		},
		{
			accessorKey: 'name',
			header: () => 'Nombre',
			cell: ({ row }) => (
				<div className={styles['certificate-name']}>
					<span className={styles['text']}>{row.original.name}</span>
					<span className={styles['serial']}>
						<span className={styles['value']}>No. de serie </span>
						{row.original.certificate_id}
					</span>
				</div>
			),
		},
		{
			accessorKey: 'expiry_date',
			header: () => 'Fecha de expiración',
			cell: ({ row }) => {
				return DateTime.fromISO(
					row.getValue('expiry_date') + 'Z',
				).toFormat('yyyy-MM-dd HH:mm');
			},
		},
		{
			accessorKey: 'status',
			header: () => 'Estado',
			cell: ({ row }) => {
				const status = row.original.status;
				const statusStyles: Record<Certificate['status'], string> = {
					Active: styles['active'],
					Inactive: styles['inactive'],
					Pending: styles['pending'],
				};
				const statusLabels: Record<Certificate['status'], string> = {
					Active: 'Activo',
					Inactive: 'Inactivo',
					Pending: 'Pendiente',
				};
				return (
					<span
						className={cn(styles['status'], statusStyles[status])}
					>
						{statusLabels[status] ?? status}
					</span>
				);
			},
		},
		{
			id: 'actions',
			cell: ({ row }) => {
				return (
					<div className={styles['actions']}>
						<Button
							variant="ghost"
							onClick={() => handleOpen(row.original)}
						>
							Editar
							<Pencil />
						</Button>
						<Button
							variant="ghost"
							onClick={() =>
								handleDownload(row.original.certificate_id)
							}
						>
							Descargar
							<Download />
						</Button>
					</div>
				);
			},
		},
	];

	return (
		<div className={styles['my-signatures']}>
			<div className={`cyber-backdrop ${styles.content}`}></div>
			<div className="cyber-main with-sidenav" onClick={hideSidebar}>
				<div className="cyber-spacer section" />
				{/** */}
				<div className={styles.container}>
					<div className={styles.body}>
						<div className={styles.header}>
							<h1>Mis firmas</h1>
							<Button
								onClick={() => {
									history.push(
										routeConfig.requestSignature +
										`?${QUERY_SIGNATURE_KEY}=${QUERY_SIGNATURE_NEW}`,
									);
								}}
							>
								<Plus />
								Nueva firma
							</Button>
						</div>
						{editResult && (
							<Alert
								variant={
									editResult.status === 'success'
										? 'default'
										: 'destructive'
								}
								className={cn(
									styles['edit-result-alert'],
									styles[
									'edit-result-alert-' + editResult.status
									],
								)}
							>
								{editResult.status === 'success' ? (
									<BadgeCheck />
								) : (
									<CircleX />
								)}
								{editResult.description}
							</Alert>
						)}
						<DataTable
							columns={myCertificatesTableData}
							data={userCertificates}
							isSortable
						/>
					</div>
					<AlertDialog
						aria-labelledby="transition-modal-title"
						aria-describedby="transition-modal-description"
						open={selectedCertificate !== null}
						onOpenChange={(open) =>
							open ? {} : setSelectedCertificate(null)
						}
					>
						<AlertDialogContent
							style={{
								padding: 24,
								maxWidth:
									window.innerWidth < 768 ? undefined : '40%',
							}}
						>
							{selectedCertificate !== null ? (
								<EditSignatureBody
									certificate={selectedCertificate}
									handleClose={handleClose}
									refreshCertificates={fetchCertificates}
									onEditComplete={setEditResult}
								/>
							) : (
								<></>
							)}
						</AlertDialogContent>
					</AlertDialog>
				</div>
			</div>
		</div>
	);
};

export default MySignatures;
